import { type ApiClient } from '../../../shared/api/api-client.js';
import { addDays, todayUtc } from '@ibe/utils';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 50;

export interface CatalogueLocationNode {
  readonly id: string;
  readonly title: string;
  readonly type: 'location' | 'hotel';
  readonly nodes?: readonly CatalogueLocationNode[] | undefined;
}

export interface CatalogueLocationResult {
  readonly id: string;
  readonly name: string;
  readonly displayName?: string | undefined;
  readonly description?: string | undefined;
  readonly imageUrl?: string | undefined;
  readonly address?: string | undefined;
  readonly starRating?: number | undefined;
  readonly currency: string;
  readonly countryCode: string;
  readonly stateCode?: string | undefined;
  readonly cityCode: string;
  readonly city: string;
  readonly logo?: string | undefined;
  readonly latitude?: string | undefined;
  readonly longitude?: string | undefined;
  readonly lowestPrice: {
    readonly baseAmount: string;
    readonly amount: string;
    readonly currency: string;
    readonly nights: number;
    readonly ratePlanId: string;
    readonly ratePlanName: string;
  };
  /**
   * Prices reachable under an eligibility condition the caller does not
   * hold — the member teaser the legacy browse card showed ("-10% off").
   * Display-only: entries never carry a quoteId and can never be booked;
   * the guest signs in and the offer is re-priced before it is selectable.
   */
  readonly conditionalPrices?: readonly CatalogueConditionalPrice[] | undefined;
  readonly roomTypes?: readonly unknown[] | undefined;
}

export interface CatalogueNightDiscount {
  readonly promotionId: string | null;
  readonly name: string;
  readonly amount: string;
}

export interface CatalogueRateBreakdownNight {
  readonly date: string;
  readonly rate: string;
  readonly baseRate: string;
  readonly ratePlanId: string;
  readonly discounts?: readonly CatalogueNightDiscount[] | undefined;
}

export interface CatalogueRoomAmenity {
  readonly name: string;
  readonly iconUrl?: string | null | undefined;
}

export interface CatalogueRate {
  /**
   * The durable server-side quote a reservation consumes — the booking
   * flow's financial authority. Present on every detailed-availability offer;
   * the reservation API uses this to derive totals server-side rather than
   * trusting client-computed money.
   */
  readonly quoteId: string;
  readonly ratePlanId: string;
  readonly ratePlanName: string;
  /** Guest-facing plan description shown under the name — legacy `rate_description`. */
  readonly ratePlanDescription?: string | null | undefined;
  readonly ratePlanType: string;
  readonly mixedRate: boolean;
  readonly rooms: number;
  readonly nights: number;
  readonly price: string;
  readonly discountedPrice: string;
  readonly discountedRoomTotal: string;
  readonly taxes: string;
  readonly fees: string;
  readonly discounts: string;
  readonly grandTotal: string;
  /**
   * Every promotion relevant to the offer. `APPLIED` entries shaped the
   * authoritative `quoteId` — `amount` is the signed discount on it.
   * `CONDITIONAL` entries are member-gated promotions an anonymous caller
   * could unlock by signing in — the entries behind the legacy rate-card
   * sale badge ("September Sale 2026 Save - 15%"). Ordered by percentage
   * descending.
   */
  readonly promotions?: readonly CatalogueOfferPromotion[] | undefined;
  /**
   * Prices reachable under an eligibility condition — today, only the
   * member teaser. Display-only: entries carry no quoteId and can never be
   * booked; the guest signs in and the offer is re-priced.
   */
  readonly conditionalPrices?: readonly CatalogueConditionalPrice[] | undefined;
  readonly currency: string;
  readonly rateBreakdown: readonly CatalogueRateBreakdownNight[];
  readonly cancellationPolicyId?: string | undefined;
  readonly cancellationPolicyName?: string | undefined;
  readonly cancellationDeadline?: string | undefined;
  readonly paymentPolicyId?: string | undefined;
  readonly paymentPolicyName?: string | undefined;
  readonly inclusions: readonly string[];
  readonly appliedPromotionIds?: readonly string[] | undefined;
  // Retained for backward compatibility only — marked deprecated in the API
  // contract. Prefer `promotions[].name`: this list's order is adjustment-label
  // order and can diverge from `appliedPromotionIds` on mixed quotes.
  readonly promotionNames?: readonly string[] | undefined;
}

/**
 * One promotion surfaced on an offer — the standardised promotion contract.
 * `APPLIED` entries shaped the bookable quote; `CONDITIONAL` entries are
 * display-only opportunities gated on `unlockCondition`.
 */
export interface CatalogueOfferPromotion {
  readonly id: string;
  readonly name: string;
  readonly type: string;
  readonly state: 'APPLIED' | 'CONDITIONAL';
  readonly unlockCondition?: 'MEMBER' | null | undefined;
  readonly percentage?: string | null | undefined;
  /** Signed applied discount — null on conditional entries. */
  readonly amount?: string | null | undefined;
  /** Application order within a stack — agrees with appliedPromotionIds. APPLIED only. */
  readonly sequence?: number | undefined;
  /**
   * Guest-actionable mechanics for the promotion's type (e.g. stay/pay
   * nights, configured fixed amount/rate, min LOS, advance bounds, cap).
   * Present only when the promotion configures them.
   */
  readonly mechanics?: CatalogueOfferPromotionMechanics | undefined;
}

export interface CatalogueOfferPromotionMechanics {
  readonly amount?: string | undefined;
  readonly stayNights?: number | undefined;
  readonly payNights?: number | undefined;
  readonly minNights?: number | undefined;
  readonly minAdvanceDays?: number | undefined;
  readonly maxAdvanceDays?: number | undefined;
  readonly fixedRate?: string | undefined;
  readonly maxDiscount?: string | undefined;
}

/**
 * A price reachable under an eligibility condition — display data, never a
 * bookable quote. The server never attaches a `quoteId` here.
 */
export interface CatalogueConditionalPrice {
  readonly condition: 'MEMBER';
  readonly promotionIds: readonly string[];
  readonly grandTotal: string;
  readonly discounts: string;
  readonly currency?: string | undefined;
}

export interface CatalogueRoomCategory {
  readonly id: string;
  readonly code: string;
  /**
   * The PMS-side category id — the legacy `category_id` value external
   * booking-engine links carry as `accommodationCode`.
   */
  readonly pmsRoomTypeId?: string | null | undefined;
  readonly name: string;
  readonly displayName?: string | undefined;
  readonly description?: string | undefined;
  readonly imageUrl?: string | undefined;
  readonly images: readonly string[];
  readonly sizeInSqm?: string | undefined;
  readonly bedType?: string | undefined;
  readonly bedTypeLongDescription?: string | undefined;
  readonly amenities: readonly CatalogueRoomAmenity[];
  readonly maxOccupancy: number;
  readonly maxAdults?: number | undefined;
  readonly maxChildren?: number | undefined;
  readonly sellableRooms: number;
  readonly resultType?: 'MATCH' | 'UPSELL' | undefined;
  readonly rates: readonly CatalogueRate[];
}

export interface CatalogueDetailedResult {
  readonly id: string;
  readonly name: string;
  readonly displayName?: string | undefined;
  readonly description?: string | undefined;
  readonly imageUrl?: string | undefined;
  readonly address?: string | undefined;
  readonly starRating?: number | undefined;
  readonly latitude?: string | undefined;
  readonly longitude?: string | undefined;
  readonly currency: string;
  readonly defaultCheckInTime?: string | undefined;
  readonly defaultCheckOutTime?: string | undefined;
  readonly minutesSinceLastBooking?: number | null | undefined;
  readonly roomTypes: readonly CatalogueRoomCategory[];
}

export interface SearchResult {
  readonly rows: readonly CatalogueLocationResult[];
  readonly totalItems: number;
}

export interface SearchStaysParams {
  readonly checkIn: string;
  readonly checkOut: string;
  readonly adults: number;
  readonly children: number;
  readonly rooms: number;
  readonly promo?: string | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
  readonly propertyId?: string | undefined;
  readonly countryCode?: string | undefined;
  readonly stateCode?: string | undefined;
  readonly cityCode?: string | undefined;
  readonly currency?: string | undefined;
}

export interface GetDetailedResultParams {
  readonly propertyId: string;
  readonly arrival: string;
  readonly departure: string;
  readonly adults: number;
  readonly children: number;
  readonly rooms: number;
  readonly promo?: string | undefined;
  readonly currency?: string | undefined;
  /**
   * No member flag — member context is server-derived from the guest's
   * verified token. A signed-in guest automatically receives member-priced
   * offers; an anonymous caller can never request them.
   */
}

/**
 * Legacy booking-engine price display: whole currency units, `Math.round`
 * half-up with an epsilon guard for binary float error, rendered as
 * `"AUD 274"`. Applied everywhere a rate or stay total is advertised.
 */
export function formatPrice(amount: string | number, currency: string): string {
  const value = Number(amount);
  if (Number.isNaN(value)) return '—';
  return `${currency.toUpperCase()} ${String(Math.round(value + Number.EPSILON))}`;
}

/**
 * The struck-through comparison price. Legacy rounded the original rate up
 * to cents before the whole-unit rounding (`Math.ceil((v + ε) * 100) / 100`),
 * so a struck price never looks lower than the discount it advertises.
 */
export function formatStrikePrice(amount: string | number, currency: string): string {
  const value = Number(amount);
  if (Number.isNaN(value)) return '—';
  const centsRoundedUp = Math.ceil((value + Number.EPSILON) * PERCENT_WHOLE) / PERCENT_WHOLE;
  return `${currency.toUpperCase()} ${String(Math.round(centsRoundedUp + Number.EPSILON))}`;
}

/**
 * The member-priced stay total an offer advertises, or null when none is
 * available. Covers both shapes: an anonymous offer carries the teaser in
 * `conditionalPrices[MEMBER].grandTotal` while its own `discounts` stays
 * zero; a signed-in member's offer is already priced at the member rate
 * (`discounts` > 0 on the authoritative quote).
 */
export function rateMemberTotal(rate: CatalogueRate): number | null {
  const teaser = rateMemberConditionalPrice(rate);
  if (teaser !== undefined && Number(teaser.grandTotal) < Number(rate.grandTotal)) {
    return Number(teaser.grandTotal);
  }
  return Number(rate.discounts) > 0 ? Number(rate.grandTotal) : null;
}

/**
 * The member teaser entry on a quote — resolved by `condition === 'MEMBER'`,
 * never by array position, so additional conditional audiences cannot
 * silently displace it. When several MEMBER entries exist the cheapest wins,
 * matching the "best advertised price" display contract.
 */
export const MEMBER_CONDITIONS: ReadonlySet<string> = new Set(['MEMBER']);

export function rateMemberConditionalPrice(
  rate: Pick<CatalogueRate, 'conditionalPrices'>,
): CatalogueConditionalPrice | undefined {
  return (rate.conditionalPrices ?? [])
    .filter((entry) => MEMBER_CONDITIONS.has(entry.condition))
    .reduce<CatalogueConditionalPrice | undefined>(
      (best, entry) =>
        best === undefined || Number(entry.grandTotal) < Number(best.grandTotal) ? entry : best,
      undefined,
    );
}

/**
 * The total the card's headline price is computed from — the member total
 * when one is advertised, else the public grand total. Sort and "best value"
 * selection must key off this so the cheapest card the guest sees wins, the
 * way the backend `displayedPrice` ordering emits offers.
 */
export function rateDisplayedTotal(rate: CatalogueRate): number {
  return rateMemberTotal(rate) ?? Number(rate.grandTotal);
}

const PERCENT_WHOLE = 100;

export interface CardPricing {
  /** Per-night headline price the card displays. */
  readonly amount: number;
  /** Per-night struck-through price, when a cheaper rate is shown. */
  readonly strikeAmount?: number;
  /** Whole-percent discount for the badge — present only when real. */
  readonly discountPercent?: number;
  readonly currency: string;
  readonly nights: number;
}

/**
 * What a property card should advertise for a stay, matching the legacy
 * browse card: a cheaper member price wins the headline with the public rate
 * struck through; otherwise the headline is the cheapest rate with the
 * pre-promotion total struck when a promotion actually applied. When nothing
 * is discounted, no strike price and no badge — the card must not fake one.
 */
export function resolveCardPricing(result: CatalogueLocationResult): CardPricing {
  const lowest = result.lowestPrice;
  const nights = Math.max(1, lowest.nights);
  const member = result.conditionalPrices?.[0] ?? null;
  const lowestAmount = Number(lowest.amount);
  const memberAmount = member !== null ? Number(member.grandTotal) : Number.POSITIVE_INFINITY;
  const memberCheaper = memberAmount < lowestAmount;

  const display = memberCheaper && member !== null ? memberAmount : lowestAmount;
  const baseAmount = Number(lowest.baseAmount);
  const strike =
    (memberCheaper ? lowestAmount : baseAmount) > display
      ? memberCheaper
        ? lowestAmount
        : baseAmount
      : undefined;
  const discountPercent =
    strike !== undefined
      ? Math.max(1, Math.round((1 - display / strike) * PERCENT_WHOLE))
      : undefined;

  return {
    amount: display / nights,
    ...(strike !== undefined ? { strikeAmount: strike / nights } : {}),
    ...(discountPercent !== undefined ? { discountPercent } : {}),
    currency: lowest.currency,
    nights,
  };
}

const DEFAULT_STAY_NIGHTS = 2;

/**
 * Stay-date handling for deep links, mirroring the legacy booking engine: a
 * link carrying both dates uses them verbatim; a link missing one or both
 * defaults to today with a two-night stay, a check-in already in the past
 * resets to the defaults, and a partial stay never drops below two nights.
 */
export function resolveStayDates(
  checkIn: string | null,
  checkOut: string | null,
): { checkIn: string; checkOut: string } {
  const today = todayUtc();
  if (checkIn !== null && checkIn !== '' && checkIn >= today) {
    const minCheckOut = addDays(checkIn, DEFAULT_STAY_NIGHTS);
    const resolvedCheckOut =
      checkOut !== null && checkOut !== '' && checkOut >= minCheckOut ? checkOut : minCheckOut;
    return { checkIn, checkOut: resolvedCheckOut };
  }
  return { checkIn: today, checkOut: addDays(today, DEFAULT_STAY_NIGHTS) };
}

export function formatStayPrice(amount: string | number, currency: string, nights: number): string {
  return formatPrice(Number(amount) * nights, currency);
}

export async function getLocationTree(api: ApiClient): Promise<readonly CatalogueLocationNode[]> {
  return await api.get<readonly CatalogueLocationNode[]>('/v1/properties/locations/tree');
}

export async function searchStays(
  api: ApiClient,
  params: SearchStaysParams,
): Promise<SearchResult> {
  const { checkIn, checkOut, promo, ...rest } = params;
  return await api.get<SearchResult>('/v1/availability/search', {
    arrival: checkIn,
    departure: checkOut,
    page: rest.page ?? DEFAULT_PAGE,
    pageSize: rest.pageSize ?? DEFAULT_PAGE_SIZE,
    ...(promo !== undefined && promo !== '' ? { promoCode: promo } : {}),
    ...rest,
  });
}

export async function getDetailedResult(
  api: ApiClient,
  params: GetDetailedResultParams,
): Promise<CatalogueDetailedResult> {
  const { promo, ...rest } = params;
  return await api.get<CatalogueDetailedResult>('/v1/availability/detailed', {
    ...rest,
    ...(promo !== undefined && promo !== '' ? { promoCode: promo } : {}),
  });
}

/**
 * The minimal public property reference returned by the deep-link resolution
 * endpoints — everything the frontend needs to route to the property view.
 */
export interface PropertyRef {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly displayName: string | null;
}

/** Legacy `propertyCode` URL parameter — resolves the property's public code. */
export async function getPropertyByCode(api: ApiClient, code: string): Promise<PropertyRef> {
  return await api.get<PropertyRef>(`/v1/properties/by-code/${encodeURIComponent(code)}`);
}

/** Legacy `property_id` URL parameter — carries the RMS-side property id. */
export async function getPropertyByPmsId(api: ApiClient, pmsId: string): Promise<PropertyRef> {
  return await api.get<PropertyRef>(`/v1/properties/by-pms-id/${encodeURIComponent(pmsId)}`);
}

/**
 * What the legacy `destination` deep-link parameter resolves to. The location
 * tree keys its nodes on the same codes the search endpoint accepts, so a
 * `destination` value maps onto the query params a node at that depth needs —
 * a hotel node is a property, anything higher is a location search.
 */
export type DestinationResolution =
  | { readonly propertyId: string }
  | {
      readonly countryCode: string;
      readonly stateCode?: string | undefined;
      readonly cityCode?: string | undefined;
    };

export function resolveDestinationNode(
  nodes: readonly CatalogueLocationNode[],
  destination: string,
): DestinationResolution | null {
  for (const country of nodes) {
    if (country.id === destination) return { countryCode: country.id };
    for (const state of country.nodes ?? []) {
      if (state.id === destination && state.id !== '_none') {
        return { countryCode: country.id, stateCode: state.id };
      }
      for (const city of state.nodes ?? []) {
        if (city.id === destination) {
          return {
            countryCode: country.id,
            ...(state.id === '_none' ? {} : { stateCode: state.id }),
            cityCode: city.id,
          };
        }
        for (const hotel of city.nodes ?? []) {
          if (hotel.id === destination) return { propertyId: hotel.id };
        }
      }
    }
  }
  return null;
}
